import { useEffect, useState } from 'react';

interface JiraiCapsuleLoadingProps {
  imageUrl: string;
  onComplete: () => void;
}

export default function JiraiCapsuleLoading({ imageUrl, onComplete }: JiraiCapsuleLoadingProps) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);
    const t2 = setTimeout(() => setPhase(2), 1800);
    const t3 = setTimeout(() => setPhase(3), 2600);
    const t4 = setTimeout(() => onComplete(), 4400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        background: 'rgba(8, 2, 6, 0.97)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* 背景の光彩 */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(225,29,72,0.12) 0%, transparent 70%)',
          transition: 'opacity 1s ease',
          opacity: phase >= 2 ? 1 : 0,
        }}
      />

      {/* Phase 0: テキスト */}
      <p
        className="text-white text-sm tracking-[0.25em] font-light absolute"
        style={{
          fontFamily: "'M PLUS Rounded 1c', sans-serif",
          top: '28%',
          opacity: phase === 0 ? 1 : phase === 1 ? 0.4 : 0,
          transform: phase === 0 ? 'translateY(0)' : 'translateY(-16px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
          letterSpacing: '0.3em',
          textShadow: '0 0 20px rgba(225,29,72,0.8)',
        }}
      >
        あなたの地雷タイプが決まりました...
      </p>

      {/* カプセル本体 */}
      <div
        className="relative flex items-center justify-center"
        style={{
          width: '140px',
          height: '220px',
          opacity: phase >= 3 ? 0 : 1,
          transition: phase >= 3 ? 'opacity 0.5s ease' : 'none',
        }}
      >
        {/* 上半球（赤） */}
        <div
          className="absolute left-0 right-0 flex justify-center"
          style={{
            top: 0,
            zIndex: 10,
            transform: (() => {
              if (phase === 0) return 'translateY(-300px)';
              if (phase === 1) return 'translateY(0px)';
              if (phase >= 2) return 'translateY(-70px) rotate(-8deg)';
              return 'translateY(0px)';
            })(),
            transition: (() => {
              if (phase === 1) return 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)';
              if (phase >= 2) return 'transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)';
              return 'none';
            })(),
          }}
        >
          <CapsuleTop />
        </div>

        {/* 下半球（白/ピンク） */}
        <div
          className="absolute left-0 right-0 flex justify-center"
          style={{
            top: '110px',
            zIndex: 10,
            transform: (() => {
              if (phase === 0) return 'translateY(-300px)';
              if (phase === 1) return 'translateY(0px)';
              if (phase >= 2) return 'translateY(70px) rotate(8deg)';
              return 'translateY(0px)';
            })(),
            transition: (() => {
              if (phase === 1) return 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.05s';
              if (phase >= 2) return 'transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1)';
              return 'none';
            })(),
          }}
        >
          <CapsuleBottom />
        </div>

        {/* 割れた瞬間の光 */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '85px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,120,150,0.95) 0%, rgba(225,29,72,0.5) 40%, transparent 70%)',
            filter: 'blur(10px)',
            opacity: phase === 2 ? 1 : 0,
            transition: 'opacity 0.25s ease',
            zIndex: 5,
          }}
        />
      </div>

      {/* イラスト（カプセルが消えた後） */}
      <div
        className="absolute flex flex-col items-center justify-center"
        style={{ inset: 0, zIndex: 20, pointerEvents: 'none' }}
      >
        <div
          style={{
            width: 'min(65vw, 340px)',
            height: 'min(65vw, 340px)',
            opacity: phase >= 3 ? 1 : 0,
            transform: phase >= 3 ? 'scale(1)' : 'scale(0.6)',
            transition: phase >= 3
              ? 'opacity 0.7s cubic-bezier(0.34, 1.2, 0.64, 1), transform 0.7s cubic-bezier(0.34, 1.2, 0.64, 1)'
              : 'none',
          }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,100,130,0.25) 0%, transparent 70%)',
              filter: 'blur(16px)',
              transform: 'scale(1.3)',
            }}
          />
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="診断結果"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center top',
                display: 'block',
                position: 'relative',
                zIndex: 1,
              }}
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ position: 'relative', zIndex: 1 }}
            >
              <i className="ri-heart-2-fill text-6xl" style={{ color: '#e11d48' }} />
            </div>
          )}
        </div>
      </div>

      {/* パーティクル */}
      {phase >= 2 && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 15 }}>
          {JIRAI_PARTICLES.map((p) => (
            <div
              key={p.id}
              className="absolute rounded-full"
              style={{
                width: p.size,
                height: p.size,
                left: p.left,
                top: p.top,
                background: p.color,
                boxShadow: `0 0 ${p.glow} ${p.color}`,
                animation: `jirai-particle ${p.duration}s ease-out forwards`,
                animationDelay: `${p.delay}s`,
                opacity: 0,
                '--tx': p.tx,
                '--ty': p.ty,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes jirai-particle {
          0%   { opacity: 0; transform: translate(0, 0) scale(0); }
          15%  { opacity: 1; transform: translate(calc(var(--tx) * 0.3), calc(var(--ty) * 0.3)) scale(1.2); }
          60%  { opacity: 0.8; transform: translate(var(--tx), var(--ty)) scale(0.8); }
          100% { opacity: 0; transform: translate(calc(var(--tx) * 1.5), calc(var(--ty) * 1.5 + 30px)) scale(0.2); }
        }
      `}</style>
    </div>
  );
}

/** 上半球：丸みのある赤いドーム */
function CapsuleTop() {
  return (
    <svg width="140" height="120" viewBox="0 0 140 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="ctMain" cx="40%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ff6b8a" />
          <stop offset="35%" stopColor="#e11d48" />
          <stop offset="70%" stopColor="#be123c" />
          <stop offset="100%" stopColor="#881337" />
        </radialGradient>
        <radialGradient id="ctShine" cx="32%" cy="22%" r="45%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.75)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <linearGradient id="ctBand" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f43f5e" />
          <stop offset="100%" stopColor="#9f1239" />
        </linearGradient>
      </defs>
      {/* ドーム本体 */}
      <path d="M8 100 Q8 8 70 8 Q132 8 132 100 Z" fill="url(#ctMain)" />
      {/* 光沢 */}
      <path d="M8 100 Q8 8 70 8 Q132 8 132 100 Z" fill="url(#ctShine)" opacity="0.6" />
      {/* ハイライト楕円 */}
      <ellipse cx="48" cy="34" rx="20" ry="11" fill="rgba(255,255,255,0.55)" style={{ filter: 'blur(3px)' }} />
      <ellipse cx="42" cy="28" rx="9" ry="5" fill="rgba(255,255,255,0.80)" style={{ filter: 'blur(1px)' }} />
      {/* 小さな光点 */}
      <circle cx="96" cy="40" r="2.5" fill="rgba(255,255,255,0.85)" />
      <circle cx="108" cy="58" r="1.5" fill="rgba(255,255,255,0.65)" />
      <circle cx="30" cy="62" r="2" fill="rgba(255,255,255,0.55)" />
      {/* 接合バンド */}
      <rect x="8" y="96" width="124" height="16" rx="8" fill="url(#ctBand)" />
      <rect x="8" y="96" width="124" height="7" rx="5" fill="rgba(255,255,255,0.22)" />
    </svg>
  );
}

/** 下半球：丸みのある白/ピンクのドーム */
function CapsuleBottom() {
  return (
    <svg width="140" height="120" viewBox="0 0 140 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="cbMain" cx="40%" cy="70%" r="70%">
          <stop offset="0%" stopColor="#fff1f2" />
          <stop offset="40%" stopColor="#fecdd3" />
          <stop offset="75%" stopColor="#fda4af" />
          <stop offset="100%" stopColor="#fb7185" />
        </radialGradient>
        <radialGradient id="cbShine" cx="30%" cy="20%" r="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.70)" />
          <stop offset="60%" stopColor="rgba(255,255,255,0.20)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <linearGradient id="cbBand" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#fda4af" />
          <stop offset="100%" stopColor="#fb7185" />
        </linearGradient>
      </defs>
      {/* 接合バンド */}
      <rect x="8" y="8" width="124" height="16" rx="8" fill="url(#cbBand)" />
      <rect x="8" y="8" width="124" height="7" rx="5" fill="rgba(255,255,255,0.25)" />
      {/* ドーム本体 */}
      <path d="M8 20 L132 20 Q132 112 70 112 Q8 112 8 20 Z" fill="url(#cbMain)" />
      {/* 光沢 */}
      <path d="M8 20 L132 20 Q132 112 70 112 Q8 112 8 20 Z" fill="url(#cbShine)" opacity="0.55" />
      {/* ハイライト */}
      <ellipse cx="46" cy="38" rx="18" ry="9" fill="rgba(255,255,255,0.50)" style={{ filter: 'blur(2px)' }} />
      <circle cx="100" cy="46" r="2" fill="rgba(255,255,255,0.75)" />
      <circle cx="34" cy="64" r="1.5" fill="rgba(255,255,255,0.55)" />
      <circle cx="112" cy="68" r="2.5" fill="rgba(255,255,255,0.45)" />
    </svg>
  );
}

const JIRAI_PARTICLES = [
  { id: 1,  size: '8px',  left: '42%', top: '44%', color: 'rgba(255,100,130,0.95)', glow: '6px', tx: '-80px', ty: '-100px', duration: 1.4, delay: 0.0 },
  { id: 2,  size: '5px',  left: '52%', top: '42%', color: 'rgba(255,180,190,0.90)', glow: '4px', tx: '60px',  ty: '-120px', duration: 1.2, delay: 0.05 },
  { id: 3,  size: '7px',  left: '60%', top: '46%', color: 'rgba(225,29,72,0.95)',   glow: '5px', tx: '100px', ty: '-80px',  duration: 1.5, delay: 0.02 },
  { id: 4,  size: '4px',  left: '35%', top: '48%', color: 'rgba(255,150,170,0.85)', glow: '3px', tx: '-60px', ty: '-90px',  duration: 1.1, delay: 0.08 },
  { id: 5,  size: '6px',  left: '55%', top: '50%', color: 'rgba(255,200,210,0.90)', glow: '5px', tx: '70px',  ty: '-110px', duration: 1.3, delay: 0.03 },
  { id: 6,  size: '9px',  left: '46%', top: '52%', color: 'rgba(240,60,90,0.95)',   glow: '7px', tx: '-40px', ty: '-130px', duration: 1.6, delay: 0.0 },
  { id: 7,  size: '4px',  left: '38%', top: '43%', color: 'rgba(255,220,230,0.80)', glow: '3px', tx: '-90px', ty: '-70px',  duration: 1.0, delay: 0.1 },
  { id: 8,  size: '6px',  left: '62%', top: '49%', color: 'rgba(230,80,110,0.90)',  glow: '5px', tx: '110px', ty: '-60px',  duration: 1.4, delay: 0.06 },
  { id: 9,  size: '3px',  left: '48%', top: '40%', color: 'rgba(255,255,255,0.95)', glow: '4px', tx: '20px',  ty: '-140px', duration: 1.2, delay: 0.04 },
  { id: 10, size: '5px',  left: '44%', top: '55%', color: 'rgba(200,30,60,0.85)',   glow: '4px', tx: '-50px', ty: '60px',   duration: 1.1, delay: 0.07 },
  { id: 11, size: '4px',  left: '57%', top: '44%', color: 'rgba(255,170,185,0.80)', glow: '3px', tx: '80px',  ty: '50px',   duration: 1.0, delay: 0.09 },
  { id: 12, size: '7px',  left: '50%', top: '47%', color: 'rgba(215,40,70,0.95)',   glow: '6px', tx: '-20px', ty: '-150px', duration: 1.5, delay: 0.01 },
];
